import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, MoreVertical, Clock, ArrowLeft, Check, CheckCheck, Minus } from 'lucide-react';
import { useChatNotifications } from '../hooks/useChatNotifications';
import { useAuth } from '../contexts/AuthContext';
import socketService from '../services/socketService';
import PredefinedMessages from './PredefinedMessages';
import axios from 'axios';

interface ChatRoom {
  id: string;
  book_request_id: string;
  requester_id: string;
  owner_id: string;
  is_active: boolean;
  created_at: string;
  closed_at?: string;
  book_requests?: {
    id: string;
    status: string;
    books?: {
      title: string;
      author: string;
      cover_image?: string;
    };
  };
  requester?: {
    id: string;
    name: string;
  };
  owner?: {
    id: string;
    name: string;
  };
  messages?: Message[];
  unread_count?: number;
}

interface Message {
  id: string;
  content: string;
  message_type: string;
  created_at: string;
  is_read: boolean;
  sender?: {
    id: string;
    name: string;
  };
}

interface ChatPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onMinimizeChange?: (isMinimized: boolean) => void;
  isMinimized?: boolean;
}

const ChatPopup: React.FC<ChatPopupProps> = ({ isOpen, onClose, onMinimizeChange, isMinimized: externalIsMinimized }) => {
  const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showMobileChat, setShowMobileChat] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const { unreadCount, refreshNotifications, markAsRead } = useChatNotifications();
  const { user } = useAuth();

  // Format owner name (first name + last initial)
  const formatOwnerName = (fullName: string) => {
    if (!fullName) return '';
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
  };

  // Drag & Drop handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget) return; // Only drag from header
    e.preventDefault();
    
    const rect = popupRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      setIsDragging(true);
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    
    setPosition({
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Minimize/maximize handlers
  const handleMinimize = () => {
    setIsMinimized(true);
    onMinimizeChange?.(true);
  };

  const handleMaximize = () => {
    setIsMinimized(false);
    onMinimizeChange?.(false);
  };

  const handleClose = () => {
    setIsMinimized(false);
    onClose();
  };

  // Fetch chat rooms
  const fetchChatRooms = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get('/api/chat/rooms');
      setChatRooms(response.data);
    } catch (error) {
      console.error('Error fetching chat rooms:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch messages for a specific room
  const fetchMessages = async (room: ChatRoom) => {
    try {
      const response = await axios.get(`/api/chat/room/${room.book_request_id}`);
      setMessages(response.data.messages || []);
      
      // Mark messages as read
      await axios.post(`/api/chat/mark-read/${room.book_request_id}`);
      const previousUnreadCount = room.unread_count || 0;
      markAsRead(previousUnreadCount); // Decrement global count by the room's unread count
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // Send a message
  const sendMessage = async () => {
    console.log('🚀 sendMessage called', { 
      newMessage: newMessage.trim(), 
      selectedRoom: selectedRoom?.id, 
      isSending 
    });
    
    if (!newMessage.trim() || !selectedRoom || isSending) {
      console.log('❌ sendMessage blocked', { 
        hasMessage: !!newMessage.trim(), 
        hasRoom: !!selectedRoom, 
        isSending 
      });
      return;
    }

    try {
      setIsSending(true);
      console.log('📤 Sending message via Socket.io');
      
      // Send via Socket.io
      socketService.sendMessage(selectedRoom.book_request_id, newMessage.trim());
      setNewMessage('');
      
      // Scroll to bottom after sending (with longer delay to ensure message is added)
      setTimeout(() => {
        scrollToBottom();
      }, 300);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsSending(false);
    }
  };

  // Update unread count for a specific room
  const updateRoomUnreadCount = (roomId: string, count: number) => {
    setChatRooms(prev => prev.map(room => 
      room.id === roomId ? { ...room, unread_count: count } : room
    ));
  };

  // Handle room selection
  const handleRoomSelect = (room: ChatRoom) => {
    // Leave previous room if any
    if (selectedRoom) {
      socketService.leaveChatRoom(selectedRoom.book_request_id);
    }
    
    setSelectedRoom(room);
    setShowMobileChat(true); // Show chat on mobile
    
    // Join new room via Socket.io
    socketService.joinChatRoom(room.book_request_id);
    
    // Mark messages as read and update local unread count
    socketService.markMessagesAsRead(room.book_request_id);
    const previousUnreadCount = room.unread_count || 0;
    updateRoomUnreadCount(room.id, 0); // Reset unread count locally
    markAsRead(previousUnreadCount); // Decrement global count by the room's unread count
    
    // Scroll to bottom when selecting a room
    setTimeout(() => {
      scrollToBottom();
    }, 300);
    
    // Marquer aussi les messages comme lus après un court délai pour s'assurer
    setTimeout(() => {
      socketService.markMessagesAsRead(room.book_request_id);
    }, 1000);
  };

  // Scroll to bottom of messages
  const scrollToBottom = () => {
      // console.log('📜 Scrolling to bottom');
    
    // Method 1: Scroll to the end ref element
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
    
    // Method 2: Scroll the container to bottom
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
      // console.log('📜 Container scrolled to bottom', { scrollTop: container.scrollTop, scrollHeight: container.scrollHeight });
    }
  };

  // Auto-scroll when messages change
  useEffect(() => {
    if (messages.length > 0) {
      // console.log('📜 Messages changed, scrolling to bottom');
      setTimeout(() => {
        scrollToBottom();
      }, 200);
    }
  }, [messages.length]);

  useEffect(() => {
    if (isOpen) {
      fetchChatRooms();
      
      // Connect to Socket.io if user is logged in
      const token = localStorage.getItem('token');
      if (token && user) {
        socketService.connect(token);
      }
    } else {
      // Disconnect when popup is closed
      socketService.disconnect();
      // Reset minimized state when popup is closed
      setIsMinimized(false);
    }
  }, [isOpen, user]);

  // Sync external minimized state with internal state
  useEffect(() => {
    if (externalIsMinimized !== undefined) {
      setIsMinimized(externalIsMinimized);
    }
  }, [externalIsMinimized]);

  // Drag & Drop event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragOffset]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Socket.io event listeners
  useEffect(() => {
    if (!isOpen) return;

    // Listen for new messages
    socketService.onNewMessage((message) => {
      console.log('📨 New message received:', message);
      setMessages(prev => {
        const newMessages = [...prev, message];
        console.log('📝 Updated messages:', newMessages);
        return newMessages;
      });
      
      // Scroll to bottom when new message arrives (with longer delay)
      setTimeout(() => {
        scrollToBottom();
      }, 200);
      
      // Si c'est un message de l'autre utilisateur et qu'on est dans la room, marquer comme lu
      if (message.sender?.id !== user?.id && selectedRoom) {
        // Marquer automatiquement comme lu immédiatement
        socketService.markMessagesAsRead(selectedRoom.book_request_id);
      } else if (message.sender?.id !== user?.id) {
        // Si c'est un message de l'autre utilisateur mais qu'on n'est pas dans cette room, incrémenter le compteur
        // On utilise le book_request_id pour trouver la room correspondante
        const room = chatRooms.find(r => {
          // Si on a le chat_room_id dans le message, on l'utilise
          if (message.chat_room_id) {
            return r.id === message.chat_room_id;
          }
          // Sinon, on essaie de trouver par book_request_id (fallback)
          return r.book_request_id === message.book_request_id;
        });
        if (room) {
          updateRoomUnreadCount(room.id, (room.unread_count || 0) + 1);
        }
      }
    });

    // Listen for chat history
    socketService.onChatHistory((historyMessages) => {
      setMessages(historyMessages);
      
      // Scroll to bottom when loading chat history
      setTimeout(() => {
        scrollToBottom();
      }, 200);
      
      // Marquer automatiquement les messages non lus comme lus quand on reçoit l'historique
      const hasUnreadMessages = historyMessages.some(msg => 
        !msg.is_read && msg.sender?.id !== user?.id
      );
      
      if (hasUnreadMessages && selectedRoom) {
        // Marquer comme lu après un court délai pour s'assurer que l'historique est chargé
        setTimeout(() => {
          socketService.markMessagesAsRead(selectedRoom.book_request_id);
        }, 500);
      }
    });

    // Listen for messages marked as read
    socketService.onMessagesMarkedRead((data) => {
      console.log('📖 [Client] Messages marked as read event received:', data);
      setMessages(prev => {
        const updatedMessages = prev.map(msg => 
          data.messageIds.includes(msg.id) 
            ? { ...msg, is_read: true, read_at: data.readAt }
            : msg
        );
        console.log('📝 [Client] Updated messages after read status:', updatedMessages);
        return updatedMessages;
      });
      
      // Mettre à jour le compteur de messages non lus pour la room actuelle
      if (selectedRoom) {
        updateRoomUnreadCount(selectedRoom.id, 0);
      }
    });

    // Listen for errors
    socketService.onError((error) => {
      console.error('Socket error:', error);
    });

    // Cleanup listeners when component unmounts or popup closes
    return () => {
      socketService.removeAllListeners();
    };
  }, [isOpen]);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
    }
  };

  const getMessageStatus = (message: Message) => {
    if (!message.is_read) {
      return { icon: Check, color: 'text-gray-400', text: 'Envoyé' };
    } else {
      return { icon: CheckCheck, color: 'text-forest-600', text: 'Lu' };
    }
  };

  if (!isOpen) return null;

  if (!isOpen) return null;

  return (
    <>
      {/* Floating button when minimized */}
      {isMinimized && (
        <button
          onClick={handleMaximize}
          className="fixed bottom-4 right-4 bg-forest-600 text-white p-3 rounded-full shadow-lg hover:bg-forest-700 transition-colors z-50"
        >
          <MessageCircle className="h-6 w-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Main popup */}
      {!isMinimized && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 p-2 sm:p-4">
          <div 
            ref={popupRef}
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[90vh] sm:h-[600px] flex flex-col absolute"
            style={{ 
              left: position.x || '50%',
              top: position.y || '50%',
              transform: position.x && position.y ? 'none' : 'translate(-50%, -50%)',
              cursor: isDragging ? 'grabbing' : 'default'
            }}
          >
            {/* Header - Draggable */}
            <div 
              className="flex items-center justify-between p-4 border-b border-gray-200 cursor-grab active:cursor-grabbing"
              onMouseDown={handleMouseDown}
            >
              <div className="flex items-center space-x-2">
                <MessageCircle className="h-5 w-5 text-forest-700" />
                <h2 className="text-lg font-semibold text-gray-900">Messages</h2>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleMinimize}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  title="Réduire"
                >
                  <Minus className="h-5 w-5" />
                </button>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  title="Fermer"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Chat Rooms List */}
          <div className="w-full sm:w-1/3 border-r border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900">Conversations</h3>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-gray-500">
                  <Clock className="h-6 w-6 mx-auto mb-2 animate-spin" />
                  Chargement...
                </div>
              ) : chatRooms.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  Aucune conversation
                </div>
              ) : (
                chatRooms.map((room) => (
                  <div
                    key={room.id}
                    onClick={() => handleRoomSelect(room)}
                    className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedRoom?.id === room.id ? 'bg-forest-50 border-forest-200' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-forest-100 to-forest-200 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MessageCircle className="h-5 w-5 text-forest-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-medium text-gray-900 truncate">
                            {room.book_requests?.books?.title || 'Livre'}
                          </h4>
                          {(room.unread_count || 0) > 0 && (
                            <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                              {room.unread_count}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 truncate">
                          {room.book_requests?.books?.author || 'Auteur'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {room.requester?.id === user?.id 
                            ? formatOwnerName(room.owner?.name || '')
                            : formatOwnerName(room.requester?.name || '')
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Chat Messages */}
          <div className="hidden sm:flex flex-1 flex-col">
            {selectedRoom ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">
                        {selectedRoom.book_requests?.books?.title}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {selectedRoom.book_requests?.books?.author}
                      </p>
                    </div>
                    {/* <button className="text-gray-400 hover:text-gray-600">
                      <MoreVertical className="h-4 w-4" />
                    </button> */}
                  </div>
                </div>

                {/* Messages */}
                <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-2">
                  {messages.map((message, index) => {
                    const isOwnMessage = message.sender?.id === user?.id;
                    const status = getMessageStatus(message);
                    const StatusIcon = status.icon;
                    const previousMessage = index > 0 ? messages[index - 1] : null;
                    const showName = !previousMessage || previousMessage.sender?.id !== message.sender?.id;
                    
                    return (
                      <div
                        key={message.id}
                        className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className="max-w-xs lg:max-w-md">
                          {/* Nom de l'expéditeur (seulement si c'est un nouveau message de cette personne) */}
                          {!isOwnMessage && showName && (
                            <p className="text-xs text-gray-600 mb-1 px-1">
                              {formatOwnerName(message.sender?.name || '')}
                            </p>
                          )}
                          
                          {/* Bulle de message avec style directionnel */}
                          <div className="relative">
                            <div
                              className={`px-3 py-2 rounded-2xl ${
                                isOwnMessage
                                  ? 'bg-green-500 text-white rounded-br-md'
                                  : 'bg-white text-gray-900 rounded-bl-md border border-gray-200'
                              }`}
                            >
                              <p className="text-sm leading-relaxed">{message.content}</p>
                            </div>
                            
                            {/* Petite flèche directionnelle */}
                            <div
                              className={`absolute top-0 w-0 h-0 ${
                                isOwnMessage
                                  ? 'right-0 transform translate-x-1 border-l-4 border-l-green-500 border-t-4 border-t-transparent border-b-4 border-b-transparent'
                                  : 'left-0 transform -translate-x-1 border-r-4 border-r-white border-t-4 border-t-transparent border-b-4 border-b-transparent'
                              }`}
                            />
                          </div>
                          
                          {/* Timestamp et statut */}
                          <div className={`flex items-center mt-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                            <p className={`text-xs ${
                              isOwnMessage ? 'text-gray-500' : 'text-gray-400'
                            }`}>
                              {formatTime(message.created_at)}
                            </p>
                            {isOwnMessage && (
                              <div className="ml-2 flex items-center">
                                <StatusIcon className={`h-3 w-3 ${status.color}`} />
                                <span className={`text-xs ml-1 ${status.color}`}>
                                  {status.text}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <div className="p-4 border-t border-gray-200">
                  {/* Predefined Messages */}
                  <div className="mb-3">
                    <PredefinedMessages 
                      onSelectMessage={(message) => setNewMessage(message)}
                      disabled={isSending}
                      bookTitle={selectedRoom?.book_requests?.books?.title}
                      otherUserName={selectedRoom?.requester?.id === user?.id 
                        ? selectedRoom?.owner?.name 
                        : selectedRoom?.requester?.name}
                    />
                  </div>
                  
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                      placeholder="Tapez votre message..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent"
                      disabled={isSending}
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!newMessage.trim() || isSending}
                      className="px-4 py-2 bg-forest-600 text-white rounded-lg hover:bg-forest-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Send className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <MessageCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Sélectionnez une conversation</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Chat View */}
        {showMobileChat && selectedRoom && (
          <div className="sm:hidden fixed inset-0 bg-white z-50 flex flex-col">
            {/* Mobile Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
              <button
                onClick={() => setShowMobileChat(false)}
                className="text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex-1 text-center">
                <h3 className="text-sm font-medium text-gray-900">
                  {selectedRoom.book_requests?.books?.title}
                </h3>
                <p className="text-xs text-gray-500">
                  {selectedRoom.book_requests?.books?.author}
                </p>
              </div>
              <div className="w-5"></div> {/* Spacer */}
            </div>

            {/* Mobile Messages */}
            <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-2">
              {messages.map((message, index) => {
                const isOwnMessage = message.sender?.id === user?.id;
                const status = getMessageStatus(message);
                const StatusIcon = status.icon;
                const previousMessage = index > 0 ? messages[index - 1] : null;
                const showName = !previousMessage || previousMessage.sender?.id !== message.sender?.id;
                
                return (
                  <div
                    key={message.id}
                    className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className="max-w-[80%]">
                      {/* Nom de l'expéditeur (seulement si c'est un nouveau message de cette personne) */}
                      {!isOwnMessage && showName && (
                        <p className="text-xs text-gray-600 mb-1 px-1">
                          {formatOwnerName(message.sender?.name || '')}
                        </p>
                      )}
                      
                      {/* Bulle de message avec style directionnel */}
                      <div className="relative">
                        <div
                          className={`px-3 py-2 rounded-2xl ${
                            isOwnMessage
                              ? 'bg-green-500 text-white rounded-br-md'
                              : 'bg-white text-gray-900 rounded-bl-md border border-gray-200'
                          }`}
                        >
                          <p className="text-sm leading-relaxed">{message.content}</p>
                        </div>
                        
                        {/* Petite flèche directionnelle */}
                        <div
                          className={`absolute top-0 w-0 h-0 ${
                            isOwnMessage
                              ? 'right-0 transform translate-x-1 border-l-4 border-l-green-500 border-t-4 border-t-transparent border-b-4 border-b-transparent'
                              : 'left-0 transform -translate-x-1 border-r-4 border-r-white border-t-4 border-t-transparent border-b-4 border-b-transparent'
                          }`}
                        />
                      </div>
                      
                      {/* Timestamp et statut */}
                      <div className={`flex items-center mt-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                        <p className={`text-xs ${
                          isOwnMessage ? 'text-gray-500' : 'text-gray-400'
                        }`}>
                          {formatTime(message.created_at)}
                        </p>
                        {isOwnMessage && (
                          <div className="ml-2 flex items-center">
                            <StatusIcon className={`h-3 w-3 ${status.color}`} />
                            <span className={`text-xs ml-1 ${status.color}`}>
                              {status.text}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Mobile Message Input */}
            <div className="p-4 border-t border-gray-200 bg-white">
              {/* Predefined Messages */}
              <div className="mb-3">
                <PredefinedMessages 
                  onSelectMessage={(message) => setNewMessage(message)}
                  disabled={isSending}
                  bookTitle={selectedRoom?.book_requests?.books?.title}
                  otherUserName={selectedRoom?.requester?.id === user?.id 
                    ? selectedRoom?.owner?.name 
                    : selectedRoom?.requester?.name}
                />
              </div>
              
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  placeholder="Tapez votre message..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent"
                  disabled={isSending}
                />
                <button
                  onClick={sendMessage}
                  disabled={!newMessage.trim() || isSending}
                  className="px-4 py-2 bg-forest-600 text-white rounded-lg hover:bg-forest-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
          </div>
        </div>
      )}
    </>
  );
};

export default ChatPopup;
